import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppModal from '../components/AppModal';
import Breadcrumb from '../components/Breadcrumb';
import Toast, { useToasts } from '../components/Toast';
import { API_BASE_URL } from '../api';
import { canWriteTestCases } from '../permissions';
import './TestCasePage.css';

/** A single test suite and the test cases under it. */
function SuiteDetailPage({ user }) {
  const { projectId, suiteId } = useParams();
  const navigate = useNavigate();
  const canWrite = canWriteTestCases(user);
  const token = localStorage.getItem('token');
  const { toasts, showToast, dismissToast } = useToasts();

  const [project, setProject] = useState(null);
  const [suite, setSuite] = useState(null);
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showImportForm, setShowImportForm] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importingCsv, setImportingCsv] = useState(false);
  const importingCsvRef = useRef(false);

  const [modalState, setModalState] = useState({ open: false, title: '', message: '' });
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deletingTestCase, setDeletingTestCase] = useState(false);
  const deletingTestCaseRef = useRef(false);

  const showModal = (title, message) => setModalState({ open: true, title, message });
  const closeModal = () => setModalState({ open: false, title: '', message: '' });

  const fetchTestCases = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/testcases/suite/${suiteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setTestCases(await response.json());
    } catch (err) {
      console.error('Error fetching test cases:', err);
    }
  }, [suiteId, token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE_URL}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_BASE_URL}/suites/${suiteId}`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_BASE_URL}/testcases/suite/${suiteId}`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(async ([projectRes, suiteRes, casesRes]) => {
        if (cancelled) return;
        if (suiteRes.status === 404) {
          setError('This test suite could not be found.');
          return;
        }
        if (projectRes.ok) setProject(await projectRes.json());
        if (suiteRes.ok) setSuite(await suiteRes.json());
        if (casesRes.ok) setTestCases(await casesRes.json());
      })
      .catch((err) => {
        console.error('Error loading suite:', err);
        if (!cancelled) setError('Something went wrong loading this test suite.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, suiteId, token]);

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!csvFile || importingCsvRef.current) return;
    importingCsvRef.current = true;
    setImportingCsv(true);

    const formDataObj = new FormData();
    formDataObj.append('file', csvFile);

    try {
      const response = await fetch(`${API_BASE_URL}/import/${suiteId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataObj,
      });

      const contentType = response.headers.get('content-type') || '';
      const result = contentType.includes('application/json')
        ? await response.json()
        : { error: (await response.text())?.slice(0, 300) || 'Unexpected non-JSON response from server.' };

      if (response.ok) {
        showToast(`Imported ${result.successCount} test cases successfully. ${result.errorCount} errors.`);
        setCsvFile(null);
        setShowImportForm(false);
        fetchTestCases();
      } else if (response.status === 403) {
        showModal('Access Denied', result.error || 'You do not have permission to import test cases.');
      } else {
        showModal('Import Failed', result.error || `Failed to import CSV file (HTTP ${response.status}).`);
      }
    } catch (err) {
      console.error('Error importing CSV:', err);
      showModal('Import Failed', 'Error importing CSV: ' + err.message);
    } finally {
      importingCsvRef.current = false;
      setImportingCsv(false);
    }
  };

  const confirmDeleteTestCase = async () => {
    if (deletingTestCaseRef.current) return;
    deletingTestCaseRef.current = true;
    setDeletingTestCase(true);
    try {
      const response = await fetch(`${API_BASE_URL}/testcases/${pendingDeleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showToast('Test case deleted successfully.');
        fetchTestCases();
      } else if (response.status === 403) {
        showModal('Access Denied', data.error || 'You do not have permission to delete test cases.');
      } else {
        showModal('Delete Failed', data.error || 'Error deleting test case.');
      }
    } catch (err) {
      console.error('Error deleting test case:', err);
      showModal('Delete Failed', 'Error deleting test case: ' + err.message);
    } finally {
      deletingTestCaseRef.current = false;
      setDeletingTestCase(false);
      setPendingDeleteId(null);
    }
  };

  const caseBasePath = `/projects/${projectId}/suites/${suiteId}/cases`;

  if (loading) {
    return <div className="test-case-page"><div className="container"><p className="loading">Loading...</p></div></div>;
  }

  return (
    <div className="test-case-page">
      <div className="container">
        <Breadcrumb
          items={[
            { label: 'Projects', to: '/projects' },
            { label: project?.name || `Project ${projectId}`, to: `/projects/${projectId}` },
            { label: suite?.name || `Suite ${suiteId}` },
          ]}
        />

        {error && <div className="error-banner">{error}</div>}

        {suite && (
          <>
            <h1>{suite.name}</h1>
            <p className="page-description">{suite.description || 'Manage the test cases in this suite.'}</p>

            {canWrite && (
              <div className="action-buttons">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`${caseBasePath}/new`)}
                >
                  + New Test Case
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportForm((s) => !s)}
                >
                  {showImportForm ? 'Cancel' : 'Import CSV'}
                </button>
              </div>
            )}

            {showImportForm && (
              <div className="card form-card">
                <h2>Import Test Cases from CSV</h2>
                <form onSubmit={handleImportCSV}>
                  <div className="form-group">
                    <label htmlFor="csvFile">CSV File</label>
                    <input
                      id="csvFile"
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                      required
                    />
                  </div>
                  <p className="help-text">
                    CSV should have columns: Test Case Name, Description, Preconditions, Expected Result, Priority, Category, Steps
                  </p>
                  <button type="submit" className="btn btn-primary" disabled={importingCsv}>
                    {importingCsv ? 'Importing...' : 'Import'}
                  </button>
                </form>
              </div>
            )}

            {testCases.length === 0 ? (
              <div className="empty-state">
                <p>No test cases in this suite yet.{canWrite ? ' Add one manually or import a CSV file.' : ''}</p>
              </div>
            ) : (
              <div className="test-cases-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Priority</th>
                      <th>Category</th>
                      <th>Steps</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testCases.map((tc) => (
                      <tr key={tc.id}>
                        <td>{tc.name}</td>
                        <td><span className={`priority ${tc.priority.toLowerCase()}`}>{tc.priority}</span></td>
                        <td>{tc.category || '-'}</td>
                        <td>{tc.steps && tc.steps.length > 0 ? `${tc.steps.length} step${tc.steps.length === 1 ? '' : 's'}` : '-'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-small btn-secondary"
                            onClick={() => navigate(`${caseBasePath}/${tc.id}`)}
                          >
                            View details
                          </button>
                          {canWrite && (
                            <>
                              <button
                                type="button"
                                className="btn btn-small"
                                onClick={() => navigate(`${caseBasePath}/${tc.id}/edit`)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-danger btn-small"
                                onClick={() => setPendingDeleteId(tc.id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {modalState.open && (
        <AppModal open={modalState.open} title={modalState.title} message={modalState.message} onClose={closeModal} />
      )}

      {pendingDeleteId && (
        <AppModal
          open={Boolean(pendingDeleteId)}
          title="Delete test case?"
          message="This action cannot be undone."
          onClose={() => setPendingDeleteId(null)}
          actions={[
            { label: 'Cancel', variant: 'secondary', onClick: () => setPendingDeleteId(null), disabled: deletingTestCase },
            { label: deletingTestCase ? 'Deleting...' : 'Delete', variant: 'danger', onClick: confirmDeleteTestCase, disabled: deletingTestCase },
          ]}
        />
      )}

      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

export default SuiteDetailPage;
